/**
 * Contenido de ayuda para los formularios del módulo Restaurante.
 *
 * Cada entrada alimenta a {@link RestaurantFormHelp}: un botón con ícono de
 * interrogación que abre un modal explicando para qué sirve el formulario, qué
 * significa cada campo importante y consejos de configuración.
 */

export interface RestaurantHelpField {
  /** Nombre del campo tal cual aparece en el formulario. */
  label: string;
  /** Explicación breve de qué hace el campo y cómo llenarlo. */
  text: string;
}

export interface RestaurantHelpContent {
  /** Título del modal de ayuda. */
  title: string;
  /** Uno o dos párrafos que explican para qué sirve este formulario. */
  intro: string;
  /** Campos clave del formulario. */
  fields: RestaurantHelpField[];
  /** Consejos u orden recomendado de configuración. */
  tips?: string[];
}

export type RestaurantHelpTopic =
  | 'diningRoom'
  | 'table'
  | 'preparationStation'
  | 'printer'
  | 'menuCategory'
  | 'menuItem'
  | 'recipe'
  | 'rawMaterialCategory'
  | 'rawMaterial'
  | 'inventory'
  | 'unitConversion'
  | 'purchaseOrder'
  | 'waste'
  | 'reservation'
  | 'settingsGeneral'
  | 'settingsPos'
  | 'settingsKitchen'
  | 'settingsInventory'
  | 'settingsPrinting'
  | 'settingsReservation';

export const RESTAURANT_FORM_HELP: Record<
  RestaurantHelpTopic,
  RestaurantHelpContent
> = {
  diningRoom: {
    title: 'Cómo configurar un salón',
    intro:
      'Un salón es un área física del local donde se ubican las mesas (por ejemplo "Salón principal", "Terraza" o "Barra"). Es lo primero que conviene crear: las mesas se asignan a un salón.',
    fields: [
      {
        label: 'Nombre',
        text: 'Identifica el área ante el personal. Usa nombres que el mesero reconozca de inmediato.',
      },
      {
        label: 'Descripción',
        text: 'Opcional. Notas internas, por ejemplo "solo para reservas" o "cierra en invierno".',
      },
      {
        label: 'Orden de visualización',
        text: 'Define en qué posición aparece el salón en el mapa de mesas y en los filtros. Menor número = aparece primero.',
      },
      {
        label: 'Registro activo',
        text: 'Al desactivarlo el salón deja de ofrecerse para nuevas mesas y reservas, pero no se borra su historial.',
      },
    ],
    tips: [
      'Orden sugerido de arranque: Salones → Mesas → Estaciones de preparación → Impresoras.',
      'El código del salón lo genera el sistema automáticamente.',
    ],
  },

  table: {
    title: 'Cómo configurar una mesa',
    intro:
      'Cada mesa representa un punto de atención en el salón. El POS de restaurante abre comandas por mesa y controla su estado (libre, ocupada, reservada…).',
    fields: [
      {
        label: 'Salón',
        text: 'Área a la que pertenece la mesa. Si aún no creaste salones, puedes dejarla "Sin salón asignado" y asignarla después.',
      },
      {
        label: 'Nombre',
        text: 'Etiqueta visible en el mapa de mesas (ej. "Mesa 1", "Barra 3").',
      },
      {
        label: 'Capacidad',
        text: 'Número de comensales que caben. Se usa para sugerir mesas al tomar reservas.',
      },
      {
        label: 'Forma',
        text: 'Solo afecta el dibujo de la mesa en el mapa (cuadrada, rectangular o redonda).',
      },
      {
        label: 'Estado inicial',
        text: 'Estado con el que nace la mesa. Normalmente "Libre"; usa "Fuera de servicio" si está dañada.',
      },
      {
        label: 'Posición X / Y',
        text: 'Opcional. Coordenadas para ubicar la mesa en el plano visual. Puedes ajustarlas luego arrastrando en el mapa.',
      },
      {
        label: 'Orden de visualización',
        text: 'Orden de la mesa dentro de su salón en listados.',
      },
    ],
    tips: ['El código de la mesa (M-1, M-2, …) se genera solo; no hay que escribirlo.'],
  },

  preparationStation: {
    title: 'Cómo configurar una estación de preparación',
    intro:
      'Una estación es un puesto de la cocina o barra donde se prepara un grupo de productos (ej. "Cocina caliente", "Bar", "Postres"). Al enviar la comanda, cada línea se enruta a la estación de su producto y, si tiene impresora, imprime ahí la orden.',
    fields: [
      {
        label: 'Nombre',
        text: 'Identifica el puesto ante la cocina.',
      },
      {
        label: 'Impresora',
        text: 'Opcional. Impresora donde saldrá la comanda de esta estación. Si la dejas vacía, la estación agrupa productos pero no imprime.',
      },
      {
        label: 'Minutos estimados de preparación',
        text: 'Tiempo promedio que tarda esta estación en despachar. Alimenta los tiempos que ve el mesero y el KDS.',
      },
      {
        label: 'Orden de visualización',
        text: 'Posición de la estación en el tablero de cocina (KDS) y en los selectores.',
      },
      {
        label: 'Activa',
        text: 'Si la desactivas, los productos que la usaban quedan sin enrutar hasta que elijas otra estación.',
      },
    ],
    tips: [
      'Crea primero las impresoras si quieres enlazar una a la estación.',
      'Cada ítem del menú se asocia a una estación; sin estaciones, todo cae en la cocina por defecto.',
    ],
  },

  printer: {
    title: 'Cómo configurar una impresora',
    intro:
      'Registra aquí las impresoras de comandas y tickets del restaurante. Luego se asignan a estaciones de preparación o a la caja para imprimir órdenes de cocina.',
    fields: [
      {
        label: 'Nombre',
        text: 'Cómo la reconocerá el personal (ej. "Impresora cocina", "Impresora barra").',
      },
      {
        label: 'Tipo de conexión',
        text: 'Cómo se comunica el sistema con la impresora: red (IP), USB/local o agente de impresión, según tu instalación.',
      },
      {
        label: 'Dirección / IP o identificador',
        text: 'Para impresoras de red, la IP y el puerto. Para locales, el nombre del dispositivo en el equipo.',
      },
      {
        label: 'Ancho de papel',
        text: 'Normalmente 58 mm u 80 mm. Un ancho incorrecto corta o descuadra el ticket.',
      },
      {
        label: 'Activa',
        text: 'Desactívala para dejar de enviarle trabajos sin borrar su configuración.',
      },
    ],
    tips: [
      'Prueba la impresión con una comanda real antes de dar por lista la configuración.',
      'Una misma impresora puede servir a varias estaciones.',
    ],
  },

  menuCategory: {
    title: 'Cómo configurar una categoría del menú',
    intro:
      'Las categorías agrupan los ítems del menú (ej. "Entradas", "Hamburguesas", "Bebidas") para ordenarlos en el POS y en la carta. Todo ítem del menú necesita una categoría.',
    fields: [
      {
        label: 'Nombre',
        text: 'Título del grupo tal como aparece en el POS y la carta.',
      },
      {
        label: 'Ícono',
        text: 'Opcional. Ayuda a identificar la categoría de un vistazo en el POS. Se elige de una lista de íconos de comida.',
      },
      {
        label: 'Descripción',
        text: 'Opcional. Texto de apoyo para la carta o notas internas.',
      },
      {
        label: 'Orden de visualización',
        text: 'Posición de la categoría en la lista. Menor número aparece primero.',
      },
    ],
    tips: [
      'El código de la categoría se genera automáticamente a partir del nombre.',
      'Puedes crear la categoría al vuelo desde el formulario de ítem de menú o de receta.',
    ],
  },

  menuItem: {
    title: 'Cómo configurar un ítem del menú',
    intro:
      'Un ítem del menú es un producto que se vende en el restaurante. Envuelve a un producto del catálogo general (el que lleva precio e impuestos) y le añade datos de operación: categoría, estación de cocina y disponibilidad.',
    fields: [
      {
        label: 'Producto',
        text: 'Producto del catálogo al que representa este ítem. Si no existe, créalo con "＋ Nuevo": ese producto es el que guarda precio, impuesto y unidad.',
      },
      {
        label: 'Categoría del menú',
        text: 'Grupo donde aparece en el POS. Puedes crear una categoría nueva sin salir de aquí.',
      },
      {
        label: 'Nombre visible',
        text: 'Opcional. Si lo dejas vacío se usa el nombre del producto. Úsalo para mostrar un nombre comercial distinto.',
      },
      {
        label: 'Estación de preparación',
        text: 'A qué puesto de cocina se envía la comanda de este ítem. Si no hay estaciones, puedes crear una al vuelo.',
      },
      {
        label: 'Disponible',
        text: 'Controla si se puede pedir ahora mismo. Úsalo para agotar un plato del día sin desactivar el ítem.',
      },
      {
        label: 'Destacado',
        text: 'Márcalo para resaltarlo en el POS o en la carta digital.',
      },
      {
        label: 'Tiempo de preparación (min)',
        text: 'Estimado que se muestra al mesero y al cliente.',
      },
      {
        label: 'Orden de visualización',
        text: 'Posición del ítem dentro de su categoría.',
      },
    ],
    tips: [
      'El código del ítem (ENT-001, …) lo genera el sistema.',
      'Para descontar insumos al vender, crea además una Receta ligada a este ítem.',
    ],
  },

  recipe: {
    title: 'Cómo configurar una receta',
    intro:
      'La receta define qué materias primas y en qué cantidad consume un ítem del menú. Cuando se vende o se prepara ese ítem, el sistema descuenta esos insumos del inventario de restaurante.',
    fields: [
      {
        label: 'Ítem de menú',
        text: 'Producto al que pertenece la receta. Puedes crear el ítem (y su producto y categoría) sin salir de aquí con "＋ Crear ítem de menú".',
      },
      {
        label: 'Nombre de la receta',
        text: 'Se autocompleta con el nombre del ítem, pero puedes cambiarlo (ej. "Hamburguesa clásica - montaje cocina").',
      },
      {
        label: 'Rendimiento y unidad de rendimiento',
        text: 'Cuántas porciones/unidades produce una preparación de la receta. Para venta directa suele ser 1 Unidad.',
      },
      {
        label: 'Modo de consumo',
        text: '"Consumir al preparar/vender" descuenta los insumos directamente desde la comanda. "Por lote de producción" descuenta cuando registras una producción aparte.',
      },
      {
        label: 'Ingredientes',
        text: 'Cada línea: materia prima + cantidad + unidad. La unidad del ingrediente debe poder convertirse a la unidad base de esa materia prima (revisa Conversiones de unidad si aparece un aviso).',
      },
      {
        label: 'Nota del ingrediente',
        text: 'Texto opcional por ingrediente (ej. "sin sal", "cortado en juliana"). Se abre con la flecha del pie de cada fila.',
      },
    ],
    tips: [
      'Si te falta una materia prima, créala con "＋ Crear materia prima" desde el selector de ingrediente.',
      'El botón "＋ Agregar ingrediente" al pie de la lista evita subir hasta el encabezado.',
    ],
  },

  rawMaterialCategory: {
    title: 'Cómo configurar una categoría de materia prima',
    intro:
      'Agrupa los insumos por tipo (ej. "Carnes", "Lácteos", "Vegetales", "Condimentos"). Toda materia prima necesita una categoría; también sirve para filtrar el inventario y los reportes de consumo.',
    fields: [
      {
        label: 'Nombre',
        text: 'Nombre del grupo de insumos.',
      },
      {
        label: 'Descripción',
        text: 'Opcional. Notas internas sobre qué incluye la categoría.',
      },
      {
        label: 'Orden de visualización',
        text: 'Posición en los listados y filtros de inventario.',
      },
    ],
    tips: [
      'El código de la materia prima se arma con las primeras letras de su categoría (Carnes → CAR-001).',
      'Puedes crear la categoría al vuelo desde el formulario de materia prima o de compra.',
    ],
  },

  rawMaterial: {
    title: 'Cómo configurar una materia prima',
    intro:
      'Una materia prima es un insumo que compras y consumes (carne, pan, aceite…). Al crearla, el sistema abre también su ficha de inventario. Su stock se mueve con compras, recetas y mermas.',
    fields: [
      {
        label: 'Categoría',
        text: 'Grupo del insumo. Obligatoria; puedes crear una nueva sin salir del formulario.',
      },
      {
        label: 'Unidad base',
        text: 'Unidad en la que se guarda el stock y se valoriza el costo (ej. Gramo, Mililitro, Unidad). Elige la más fina que uses en recetas para evitar decimales raros.',
      },
      {
        label: 'Nombre',
        text: 'Descripción corta del insumo tal como la reconoce la cocina.',
      },
      {
        label: 'Código',
        text: 'Opcional. Si lo dejas vacío, el sistema lo genera a partir de la categoría. Solo escríbelo si tu inventario ya usa códigos propios.',
      },
      {
        label: 'Proveedor habitual',
        text: 'Opcional. Proveedor sugerido al crear compras de este insumo.',
      },
      {
        label: 'Stock mínimo',
        text: 'Umbral para alertas de reposición. Cuando el stock cae por debajo, el insumo aparece en los avisos de bajo inventario.',
      },
      {
        label: 'Stock y costo inicial',
        text: 'Opcional. Existencia y costo con los que arranca la ficha. Después se recalculan con cada compra.',
      },
    ],
    tips: [
      'Si vas a comprar en una unidad distinta a la base (ej. comprar en Libra y guardar en Gramo), crea la regla en Conversiones de unidad.',
      'No hace falta pre-cargar todo: puedes crear la materia prima desde la compra o la receta.',
    ],
  },

  inventory: {
    title: 'Cómo ajustar el inventario',
    intro:
      'Aquí registras la existencia y los parámetros de stock de una materia prima. El flujo normal mueve el stock con compras y consumo de recetas; usa este formulario para la carga inicial o para correcciones puntuales.',
    fields: [
      {
        label: 'Materia prima',
        text: 'Insumo cuyo inventario se ajusta. Cada insumo tiene una sola ficha de inventario por sucursal.',
      },
      {
        label: 'Existencia actual',
        text: 'Cantidad física disponible, en la unidad base del insumo. Escribir un valor aquí hace un ajuste directo (queda registrado como movimiento).',
      },
      {
        label: 'Stock mínimo',
        text: 'Nivel que dispara la alerta de reposición.',
      },
      {
        label: 'Costo promedio / último costo',
        text: 'Valores con los que se valoriza el consumo. Se recalculan automáticamente con cada compra confirmada.',
      },
    ],
    tips: [
      'Para entradas por compra usa el módulo de Compras: así queda el costo y el proveedor.',
      'Para pérdidas (caducado, derrame) usa Mermas en vez de bajar el stock a mano.',
    ],
  },

  unitConversion: {
    title: 'Cómo configurar una conversión de unidad',
    intro:
      'Una conversión dice cuántas unidades "destino" hay en una unidad "origen" (ej. 1 Kilogramo = 1000 Gramo). El sistema encadena reglas automáticamente, así que con pocas reglas base cubres casi todo.',
    fields: [
      {
        label: 'Unidad origen / Unidad destino',
        text: 'El par que quieres poder convertir. La regla funciona en ambos sentidos.',
      },
      {
        label: 'Factor de conversión',
        text: 'Cuántas unidades destino equivalen a 1 unidad origen. Para "1 L = 1000 ml" el factor es 1000.',
      },
      {
        label: 'Materia prima (opcional)',
        text: 'Si la dejas vacía, la regla es global (aplica a todos los insumos). Si eliges una, la regla solo vale para ese insumo (útil para envases: "1 botella = 3 L" para un producto concreto).',
      },
      {
        label: 'Activa',
        text: 'Desactiva la regla sin borrarla si necesitas suspenderla temporalmente.',
      },
    ],
    tips: [
      'Las conversiones base de masa (kg↔g, lb↔g…), volumen (l↔ml, gal↔l) y conteo (docena, ciento) ya vienen cargadas.',
      'Si al guardar una receta o compra ves "no hay ruta de conversión", falta una regla que enlace esas dos unidades.',
    ],
  },

  purchaseOrder: {
    title: 'Cómo registrar una compra de materia prima',
    intro:
      'Una compra ingresa insumos al inventario de restaurante con su costo real. Se guarda primero como borrador y luego se confirma: al confirmar, suma el stock, actualiza costos y (si es a crédito) genera la cuenta por pagar.',
    fields: [
      {
        label: 'Proveedor',
        text: 'A quién le compras. Puedes darlo de alta con "＋ Nuevo" sin salir del formulario.',
      },
      {
        label: 'Condición de pago',
        text: 'Contado o crédito. En crédito, al confirmar se crea la cuenta por pagar con su plazo.',
      },
      {
        label: 'Líneas de detalle',
        text: 'Por cada insumo: materia prima, unidad de compra, cantidad y costo. Si el insumo no existe, créalo con "＋ Crear materia prima".',
      },
      {
        label: 'Unidad de compra',
        text: 'Puede diferir de la unidad base del insumo (comprar en Caja o Libra y guardar en Gramo). El texto de conversión muestra a cuánto equivale en la unidad base.',
      },
      {
        label: 'Costo',
        text: 'Costo total o unitario de la línea, según la columna. Alimenta el costo promedio del insumo.',
      },
    ],
    tips: [
      'Guarda como borrador para revisarla; confírmala solo cuando la mercadería llegó.',
      'Si "Unidad de compra" no lista la unidad que quieres, falta una regla en Conversiones de unidad.',
    ],
  },

  waste: {
    title: 'Cómo registrar una merma',
    intro:
      'La merma descuenta del inventario un insumo que se perdió sin venderse (caducado, derramado, dañado, error de preparación). Deja rastro para los reportes de pérdida.',
    fields: [
      {
        label: 'Materia prima',
        text: 'Insumo que se perdió.',
      },
      {
        label: 'Cantidad',
        text: 'Cuánto se perdió, en la unidad base del insumo.',
      },
      {
        label: 'Motivo',
        text: 'Causa de la pérdida (caducidad, rotura, derrame, mal manejo…). Se usa para agrupar en reportes.',
      },
      {
        label: 'Nota',
        text: 'Opcional. Detalle libre de lo ocurrido.',
      },
    ],
    tips: [
      'Usa Mermas y no un ajuste de inventario: así la pérdida queda clasificada por motivo.',
    ],
  },

  reservation: {
    title: 'Cómo registrar una reserva',
    intro:
      'Una reserva aparta una mesa para un cliente en una fecha y hora. Según la configuración de reservas, la mesa puede pasar a estado "Reservada" cerca de la hora pactada.',
    fields: [
      {
        label: 'Cliente / contacto',
        text: 'Nombre y teléfono de quien reserva, para confirmar o avisar cambios.',
      },
      {
        label: 'Fecha y hora',
        text: 'Momento de llegada previsto. La duración estimada define hasta cuándo se bloquea la mesa.',
      },
      {
        label: 'Personas',
        text: 'Número de comensales. Ayuda a sugerir una mesa con capacidad suficiente.',
      },
      {
        label: 'Mesa / salón',
        text: 'Mesa concreta o área preferida. Puedes dejar que el sistema sugiera según capacidad y disponibilidad.',
      },
      {
        label: 'Estado',
        text: 'Pendiente, confirmada, sentada, no se presentó o cancelada. Mantenlo al día para liberar mesas.',
      },
      {
        label: 'Nota',
        text: 'Peticiones especiales (cumpleaños, silla de bebé, alergia…).',
      },
    ],
    tips: [
      'Revisa Configuración → Reservas para definir antelación mínima, duración por defecto y margen entre reservas.',
    ],
  },

  settingsGeneral: {
    title: 'Configuración general del restaurante',
    intro:
      'Parámetros base que afectan a todo el módulo: identidad del servicio, moneda de propina, redondeos y comportamiento por defecto de las comandas.',
    fields: [
      {
        label: 'Servicio / propina',
        text: 'Porcentaje de servicio sugerido y si se aplica automáticamente a la cuenta.',
      },
      {
        label: 'Comensales por defecto',
        text: 'Número de personas con el que se abre una comanda si el mesero no lo indica.',
      },
      {
        label: 'Numeración de comandas',
        text: 'Cómo se numeran las órdenes (por día, correlativo continuo…).',
      },
    ],
    tips: ['Cambia estos valores con el local cerrado: afectan comandas nuevas de inmediato.'],
  },

  settingsPos: {
    title: 'Configuración del POS de restaurante',
    intro:
      'Activa o desactiva las operaciones avanzadas de mesa en el punto de venta. Cada opción habilita un botón en la comanda.',
    fields: [
      {
        label: 'Dividir cuenta',
        text: 'Permite separar una comanda en varias cuentas para cobrar por separado.',
      },
      {
        label: 'Mover mesa',
        text: 'Permite pasar toda la comanda de una mesa a otra.',
      },
      {
        label: 'Fusionar mesas',
        text: 'Une las comandas de dos o más mesas en una sola cuenta.',
      },
      {
        label: 'Mover productos',
        text: 'Permite mover líneas concretas de una comanda a otra mesa/cuenta.',
      },
    ],
    tips: [
      'Si un botón no aparece en el POS, revisa que la opción esté activa aquí y que el rol del usuario tenga permiso.',
    ],
  },

  settingsKitchen: {
    title: 'Configuración de cocina (KDS)',
    intro:
      'Controla cómo avanzan las comandas en la cocina: cierre, cola, temporizador e impresión.',
    fields: [
      {
        label: 'Impresión automática',
        text: 'Al enviar la orden, imprime en la impresora de cada estación sin intervención del mesero.',
      },
      {
        label: 'Temporizador',
        text: 'Muestra la cuenta regresiva de preparación de cada comanda en el tablero (se pone en rojo si se atrasa).',
      },
      {
        label: 'Cierre de comanda',
        text: 'Manual: la cocina pulsa «Completar comanda». Automático: se cierra al quedar todos los platos listos. Por tiempo: se cierra al agotarse su tiempo estimado total aunque falten platos; en este modo el tiempo de preparación es obligatorio en cada ítem de menú.',
      },
      {
        label: 'Avance de platos por tiempo',
        text: 'Marca cada plato como listo al cumplirse su tiempo estimado, sin cerrar la comanda.',
      },
      {
        label: 'Máx. comandas en preparación',
        text: 'Cuántas comandas pasan a preparación a la vez; el resto queda en cola y avanza sola al liberarse un lugar. 0 = sin límite.',
      },
      {
        label: 'Preparación paralela',
        text: 'Con esto apagado, un plato solo puede iniciarse cuando los anteriores de la comanda ya están listos.',
      },
    ],
    tips: [
      'El tiempo de cada plato se define en el ítem de menú; si no lo trae, se usa el estimado de su estación y, por último, el tiempo predeterminado de la sucursal (pestaña General).',
      'Necesitas estaciones de preparación con impresora para que la impresión automática funcione.',
    ],
  },

  settingsInventory: {
    title: 'Configuración de inventario de restaurante',
    intro:
      'Define cómo se descuentan los insumos y cómo se comportan las alertas y el stock negativo.',
    fields: [
      {
        label: 'Descontar insumos al vender',
        text: 'Si está activo, vender un producto con receta descuenta sus materias primas automáticamente.',
      },
      {
        label: 'Permitir stock negativo',
        text: 'Deja registrar consumo aunque no haya existencia suficiente (útil al arrancar sin inventario cargado).',
      },
      {
        label: 'Validar stock antes de preparar',
        text: 'Muestra un aviso en el POS si un ítem no tiene insumos suficientes.',
      },
      {
        label: 'Alertas de stock mínimo',
        text: 'Activa los avisos cuando un insumo cae por debajo de su stock mínimo.',
      },
    ],
    tips: [
      'Al configurar el restaurante por primera vez conviene permitir stock negativo hasta cargar las compras iniciales.',
    ],
  },

  settingsPrinting: {
    title: 'Configuración de impresión',
    intro:
      'Parámetros generales de los tickets y comandas: qué impresora usa la caja, copias y contenido del ticket.',
    fields: [
      {
        label: 'Impresora de caja / precuenta',
        text: 'Dónde salen la precuenta y el ticket del cliente.',
      },
      {
        label: 'Copias de comanda',
        text: 'Cuántas copias imprime cada estación al recibir la orden.',
      },
      {
        label: 'Datos del ticket',
        text: 'Encabezado, pie, mostrar mesero, mostrar número de comensales, etc.',
      },
    ],
    tips: ['Registra las impresoras en Restaurante → Impresoras antes de asignarlas aquí.'],
  },

  settingsReservation: {
    title: 'Configuración de reservas',
    intro:
      'Reglas que aplican a todas las reservas: cuándo se pueden tomar, cuánto dura una mesa reservada y cómo pasa la mesa a estado "Reservada".',
    fields: [
      {
        label: 'Antelación mínima / máxima',
        text: 'Con cuánto tiempo antes se puede reservar y hasta cuántos días a futuro.',
      },
      {
        label: 'Duración por defecto',
        text: 'Cuánto tiempo se bloquea la mesa si no se indica otra cosa.',
      },
      {
        label: 'Margen entre reservas',
        text: 'Minutos de colchón entre una reserva y la siguiente en la misma mesa (limpieza, montaje).',
      },
      {
        label: 'Marcar mesa como reservada',
        text: 'Cuántos minutos antes de la hora la mesa cambia automáticamente a estado "Reservada".',
      },
    ],
    tips: ['Estos valores no cambian las reservas ya creadas, solo las nuevas.'],
  },
};
